/**
 * <s-count-up data-target="48" data-duration="1800">+<span data-count>48</span></s-count-up>
 * Counts the [data-count] span up from 0 to data-target once, the first time the element
 * scrolls into view, with an ease-out curve. Server markup already shows the final number,
 * so no-JS, reduced motion and data-disabled all keep it as is.
 */
class SCountUp extends HTMLElement {
  connectedCallback() {
    if (this.observer || this.hasAttribute('data-done')) return;
    const output = this.querySelector('[data-count]');
    const target = Number.parseInt(this.dataset.target ?? '', 10);
    if (!output || !Number.isFinite(target) || target <= 0) return;
    if (this.hasAttribute('data-disabled')) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (!('IntersectionObserver' in window)) return;

    this.output = output;
    this.target = target;
    this.duration = Math.max(200, Number.parseInt(this.dataset.duration ?? '1800', 10) || 1800);
    output.textContent = '0';

    this.observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.observer?.disconnect();
        this.run();
      },
      { threshold: 0.4 },
    );
    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.observer = undefined;
    if (this.frame) cancelAnimationFrame(this.frame);
    if (this.output && !this.hasAttribute('data-done')) this.output.textContent = String(this.target);
  }

  run() {
    const start = performance.now();
    const step = (now) => {
      const progress = Math.min(1, (now - start) / this.duration);
      const eased = 1 - Math.pow(1 - progress, 3);
      this.output.textContent = String(Math.round(this.target * eased));
      if (progress < 1) {
        this.frame = requestAnimationFrame(step);
      } else {
        this.setAttribute('data-done', '');
      }
    };
    this.frame = requestAnimationFrame(step);
  }
}

if (!customElements.get('s-count-up')) customElements.define('s-count-up', SCountUp);
